import React, { FC, useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { Button, Paper, Input, AdminFormHeader } from '../../../components/UI';
import Select from 'react-select';
import { ColumnWrapper, FormColumn } from '../../Reports/Styles';
import {
  FileUploadContainer,
  FileUploadSection,
  FileUploadText,
  FileUploadTitle,
  FileUploadNote,
  FileUploadButton,
  SelectedFileInfo,
  SelectedFileName,
  FormSection,
  FormSectionTitle,
  InputGroup,
  ButtonWrapper
} from '../Styles';
import { ApplicationState } from '../../../lib/Store';
import { bulkUploadPoints, BulkUploadPointsRequest, BulkUploadPointsResponse } from '../../../lib/Api/Admin';
import { ApiStatus, APIResult } from '../../../lib/Types';
import { fetchRewardOptions } from '../../../lib/Api/Rewards';
import { Reward } from '../../../lib/Types';

interface UploadPointsFormData {
  excelFile: File | null;
  selectedReward: Reward | null;
}

interface DownloadData extends BulkUploadPointsResponse {
  eventId: number;
  reward: Reward;
  companyId?: number;
}

interface UploadPointsProps {
  onCancel: () => void;
  onSuccess: (data: DownloadData) => void;
}

const UploadPoints: FC<UploadPointsProps> = ({ onCancel, onSuccess }) => {
  // File input ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Redux
  const dispatch = useDispatch();
  const { selectedCompanyIds, selectedEventId } = useSelector(
    ({ eventCompanyPageFilterState }: ApplicationState) => ({
      selectedEventId: eventCompanyPageFilterState.selectedEventId,
      selectedCompanyIds: eventCompanyPageFilterState.selectedCompanyIds
    })
  );

  // State for rewards
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [loadingRewards, setLoadingRewards] = useState(false);

  // Fetch rewards on component mount
  useEffect(() => {
    const loadRewards = async () => {
      setLoadingRewards(true);
      try {
        const response = await dispatch(fetchRewardOptions() as any);
        if (response && response.status === ApiStatus.SUCCESS) {
          setRewards(response.data || []);
        }
      } catch (error) {
        console.error('Failed to load rewards:', error);
      } finally {
        setLoadingRewards(false);
      }
    };

    loadRewards();
  }, [dispatch]);

  // Validation schema
  const validationSchema = Yup.object({
    excelFile: Yup.mixed().required('Excel file is required'),
    selectedReward: Yup.object().required('Reward selection is required')
  });

  // Formik form
  const { values, handleSubmit, handleChange, errors, setFieldValue, isSubmitting, resetForm } = useFormik<UploadPointsFormData>({
    initialValues: {
      excelFile: null,
      selectedReward: null
    },
    validationSchema,
    validate: ({ excelFile, selectedReward }) => {
      const errors: { [key: string]: string } = {};
      if (!excelFile) errors.excelFile = 'Excel file is required.';
      if (!selectedReward) errors.selectedReward = 'Reward selection is required.';
      if (!selectedEventId) errors.excelFile = 'Event selection is required.';
      return errors;
    },
    onSubmit: async (data: UploadPointsFormData): Promise<void> => {
      if (!data.excelFile || !data.selectedReward || !selectedEventId) {
        return;
      }

      try {
        const requestData: BulkUploadPointsRequest = {
          excelFile: data.excelFile,
          eventId: selectedEventId,
          rewardId: data.selectedReward.rewardId as number
        };

        // Add companyId if a specific company is selected
        if (selectedCompanyIds?.length === 1) {
          requestData.companyId = selectedCompanyIds[0];
        }

        const response = await dispatch(bulkUploadPoints(requestData)) as unknown as APIResult<BulkUploadPointsResponse>;
        
        // Set download data for display
        if (response && response.status === ApiStatus.SUCCESS && response.data) {
          const downloadData: DownloadData = {
            ...response.data,
            eventId: selectedEventId,
            reward: data.selectedReward,
            companyId: requestData.companyId
          };
          
          onSuccess(downloadData);
        }
        
        // Reset form on success
        resetForm();
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        // Error is already handled by the API function
        console.error('Failed to upload points:', error);
      }
    }
  });

  // Event handlers
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFieldValue('excelFile', file);
  };

  const handleFileUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRewardChange = (value: string) => {
    const selectedReward = rewards.find(reward => reward.rewardId?.toString() === value);
    setFieldValue('selectedReward', selectedReward || null);
  };

  // Computed values for form validation
  const isFormValid = values.excelFile && values.selectedReward;

  // Render methods
  const renderFileUploadSection = () => (
    <FormSection>
      <FileUploadContainer>
        <FileUploadSection>
          <FileUploadText>
            <FileUploadTitle>Upload Excel File</FileUploadTitle>
            <FileUploadNote>Files should be in Excel format (.xlsx, .xls)</FileUploadNote>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <FileUploadButton
              type="button"
              variant="clear"
              onClick={handleFileUploadClick}
              disabled={isSubmitting}
            >
              {values.excelFile ? 'REPLACE' : 'UPLOAD'}
            </FileUploadButton>
          </FileUploadText>
        </FileUploadSection>
        {values.excelFile && (
          <SelectedFileInfo>
            <SelectedFileName>Selected: {values.excelFile.name}</SelectedFileName>
          </SelectedFileInfo>
        )}
        {errors.excelFile && <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{errors.excelFile}</div>}
      </FileUploadContainer>
    </FormSection>
  );

  const renderAllocationSettings = () => (
    <FormSection>
      <FormSectionTitle>Select Reward to Allocate</FormSectionTitle>
      <InputGroup>
        <Select
          classNamePrefix="react-select"
          options={rewards.map(reward => ({
            value: reward.rewardId?.toString() || '',
            label: `${reward.name} (${reward.points} points - ${reward.type})`
          }))}
          value={values.selectedReward ? {
            value: values.selectedReward.rewardId?.toString() || '',
            label: `${values.selectedReward.name} (${values.selectedReward.points} points - ${values.selectedReward.type})`
          } : null}
          placeholder={loadingRewards ? "Loading rewards..." : "Select a Reward"}
          onChange={(selectedOption) => {
            if (selectedOption) {
              const reward = rewards.find(r => r.rewardId?.toString() === selectedOption.value);
              setFieldValue('selectedReward', reward || null);
            } else {
              setFieldValue('selectedReward', null);
            }
          }}
          isSearchable={true}
          isClearable={true}
          noOptionsMessage={() => "No rewards found"}
          loadingMessage={() => "Loading rewards..."}
          styles={{
            control: (provided) => ({
              ...provided,
              minHeight: '48px',
              border: errors.selectedReward ? '1px solid #dc3545' : '1px solid #ced4da',
              borderRadius: '0.35rem',
              boxShadow: 'none',
              '&:hover': {
                border: errors.selectedReward ? '1px solid #dc3545' : '1px solid #adb5bd'
              }
            }),
            placeholder: (provided) => ({
              ...provided,
              color: '#6c757d'
            }),
            option: (provided, state) => ({
              ...provided,
              backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : 'white',
              color: state.isSelected ? 'white' : '#212529',
              '&:hover': {
                backgroundColor: state.isSelected ? '#007bff' : '#e9ecef'
              }
            })
          }}
        />
        {errors.selectedReward && (
          <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
            {errors.selectedReward}
          </div>
        )}
      </InputGroup>

    </FormSection>
  );

  const renderSubmitButton = () => (
    <ButtonWrapper>
      <Button
        type="submit"
        variant="admin-primary"
        width="25%"
        minWidth="175px"
        margin="2rem 0 1rem auto"
        disabled={!isFormValid || isSubmitting}
      >
        {isSubmitting ? 'Uploading...' : 'Allocate Reward'}
      </Button>
    </ButtonWrapper>
  );

  return (
    <form onSubmit={handleSubmit}>
      <AdminFormHeader
        title="Points Allocation"
        onClick={onCancel}
        backBtn={true}
        margin="0 0 1.5rem 0"
      />
      <Paper width="80%" margin="0 0 22rem" minWidth="792px">
        <ColumnWrapper>
          <FormColumn>
            {renderFileUploadSection()}
            {renderAllocationSettings()}
          </FormColumn>
        </ColumnWrapper>
        {renderSubmitButton()}
      </Paper>
    </form>
  );
};

export default UploadPoints;
